import { Moment } from 'moment';
import { OrderStatus as LazadaOrderStatus } from '../../services/lazada/enums/order-status';
import { OrderStatus as ShopeeOrderStatus } from '../../services/shopee/enums/order-status-v2';
import { OrderStatus as TiktokOrderStatus } from '../../services/tiktok/enums/order-status';

export enum UnifiedOrderStatus {
    Unpaid = 'unpaid',
    ToShip = 'to_ship',
    ReadyToShip = 'ready_to_ship',
    Shipped = 'shipped',
    Completed = 'completed',
    Cancelled = 'cancelled',
}

export interface PlatformStatusMapping {
    lazada: LazadaOrderStatus;
    shopee: ShopeeOrderStatus;
    tiktok: TiktokOrderStatus;
}

export const UNIFIED_STATUS_MAPPING: Record<
    UnifiedOrderStatus,
    PlatformStatusMapping
> = {
    [UnifiedOrderStatus.Unpaid]: {
        lazada: LazadaOrderStatus.Unpaid,
        shopee: ShopeeOrderStatus.Unpaid,
        tiktok: TiktokOrderStatus.Unpaid,
    },
    [UnifiedOrderStatus.ToShip]: {
        lazada: LazadaOrderStatus.Pending,
        shopee: ShopeeOrderStatus.ReadyToShip,
        tiktok: TiktokOrderStatus.AwaitingShipment,
    },
    [UnifiedOrderStatus.ReadyToShip]: {
        lazada: LazadaOrderStatus.ReadyToShip,
        shopee: ShopeeOrderStatus.Processed,
        tiktok: TiktokOrderStatus.AwaitingCollection,
    },
    [UnifiedOrderStatus.Shipped]: {
        lazada: LazadaOrderStatus.Shipped,
        shopee: ShopeeOrderStatus.Shipped,
        tiktok: TiktokOrderStatus.InTransit,
    },
    [UnifiedOrderStatus.Completed]: {
        lazada: LazadaOrderStatus.Delivered,
        shopee: ShopeeOrderStatus.Completed,
        tiktok: TiktokOrderStatus.Completed,
    },
    [UnifiedOrderStatus.Cancelled]: {
        lazada: LazadaOrderStatus.Canceled,
        shopee: ShopeeOrderStatus.Cacelled,
        tiktok: TiktokOrderStatus.Cancelled,
    },
};

// The customer details behind the receipt indicator, kept structured rather
// than pre-joined into one string so the tooltip and popup can lay them out.
export interface ReceiptDetail {
    name: string;
    taxId: string;
    branch: string;
    address: string;
    // Human-readable origin, e.g. 'chatbot' or 'recipient name/address'.
    source: string;
    // The customer asked for a full tax invoice (ใบกำกับภาษี) rather than a
    // receipt alone. Only a billing request states this — details inferred by
    // the recognizer leave it false.
    taxInvoiceRequested: boolean;
    // 'stated' — the customer supplied these themselves; 'inferred' — they
    // were worked out from the order, so they deserve a second look before a
    // tax document is issued.
    confidence: 'stated' | 'inferred';
}

export interface UnifiedOrderItem {
    itemNo: number;
    description: string;
    quantity: number;
    unitPrice: number;
    amount: number;
}

export interface UnifiedOrder {
    key: string;
    storeTitle: string;
    // The shop's id in the billing-request collector — see StoreConfig.storeId.
    storeId: string;
    platform: 'lazada' | 'shopee' | 'tiktok';
    orderId: string;
    matching: string;
    createDate: Date | null;
    status: string;
    recipientName: string;
    paymentMethod: string;
    totalAmount: number;
    itemsCount: number;
    trackingNumber: string;
    shippingCarrier: string;
    remarks: string;
    items: UnifiedOrderItem[];
    lastPrintMoment?: Moment;
    // Ready to Ship only. receiptAvailable: the invoice will be issued from the
    // customer's own billing request (Tier 0, billing-request-collector) or
    // from a confirmed tax receipt (Tier 1, any platform), rather than from
    // platform data. missingReceiptData: Shopee order with neither of those nor
    // recognized customer details (Tier 2), so the invoice will fall back to
    // the shipping-label image.
    receiptAvailable?: boolean;
    // Customer details shown in the receipt-available tooltip and popup, from
    // whichever of Tier 0/Tier 1 supplied them; set when receiptAvailable is
    // true.
    receiptDetail?: ReceiptDetail;
    missingReceiptData?: boolean;
    // Original platform order payload, needed by ship/pickup actions
    // (e.g. lazada orderItems, tiktok packages).
    raw: any;
}
